import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Paper, Avatar, Button, CircularProgress, Chip, Fade } from '@mui/material';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AuthContext from './auth/Context';

/**
 * FeatureGate — CHẮN MÀN HÌNH THUỘC GÓI MỞ RỘNG CHƯA ĐƯỢC CÀI TRÊN MÁY CHỦ.
 *
 * VẤN ĐỀ: core chạy được độc lập, các gói thương mại (AI agent, flow, marketing, usermodule...) là
 * TUỲ CHỌN. Frontend thì luôn có sẵn màn hình cho mọi gói. Khi máy chủ chưa cài gói tương ứng,
 * người dùng bấm vào và nhận về 404 / "Cannot GET /api/..." — trông như phần mềm hỏng, trong khi
 * thực tế chỉ là chưa mua/chưa cài gói đó.
 *
 * Component này biến lỗi khó hiểu ấy thành một lời nhắn rõ ràng, KÈM TÊN GÓI NPM cần cài, để quản
 * trị viên biết chính xác phải làm gì.
 *
 * TỰ ĐỘNG VỚI GÓI TƯƠNG LAI: danh sách tính năng do máy chủ trả về (`GET /api/features`) được suy ra
 * từ `optionalExtensions` trong package.json của core. Có gói mới -> chỉ cần bọc màn hình của nó
 * bằng <FeatureGate feature="ten_goi">, KHÔNG phải sửa file này.
 *
 * NGUYÊN TẮC AN TOÀN: chỉ chắn khi máy chủ NÓI RÕ là chưa cài. Chưa hỏi xong, hoặc máy chủ đời cũ
 * không có endpoint /api/features -> vẫn hiện màn hình như trước (thà hiện thừa còn hơn ẩn nhầm
 * tính năng đang chạy tốt).
 *
 *   <FeatureGate feature="usermodule"><UserModuleManager context={context}/></FeatureGate>
 *   <FeatureGate feature="marketing" context={context} compact><ChannelSettings .../></FeatureGate>
 */

/** Tên tiếng Việt cho các gói đã biết. Gói lạ vẫn chạy — chỉ là hiện thẳng khoá kỹ thuật. */
export const FEATURE_LABELS = {
    ai_agent: 'Trợ lý AI',
    flow: 'Luồng tự động (Flow)',
    marketing: 'Marketing',
    usermodule: 'Module người dùng',
    storage_usage: 'Theo dõi dung lượng dữ liệu',
};

export function featureLabel(feature) {
    return FEATURE_LABELS[feature] || feature;
}

/**
 * Đọc trạng thái một tính năng từ AuthContext.
 * @param {string} feature khoá tính năng ('usermodule', 'marketing'...) hoặc thẳng tên gói npm
 * @param {object} [ctxProp] context truyền qua prop (một số màn hình nhận context dạng prop chứ
 *                           không lấy từ AuthContext) — có thì ưu tiên dùng.
 */
export function useFeature(feature, ctxProp) {
    const ctxFromProvider = useContext(AuthContext);
    // Ưu tiên context có hasFeature thật; tránh trường hợp prop là object rỗng nuốt mất context thật.
    const context = (ctxProp && typeof ctxProp.hasFeature === 'function') ? ctxProp : (ctxFromProvider || {});

    const hasFeature = typeof context.hasFeature === 'function' ? context.hasFeature : null;
    const loadedFn = typeof context.featuresLoaded === 'function' ? context.featuresLoaded : null;

    // Chưa hỏi xong -> chờ. Nhưng KHÔNG chờ mãi: nếu vì lý do nào đó không ai nạp năng lực máy chủ
    // (component dùng ngoài Provider chẳng hạn) thì sau 3 giây coi như "không biết" và hiện màn hình.
    const [waited, setWaited] = useState(false);
    const loaded = loadedFn ? loadedFn() : true;
    useEffect(() => {
        if (loaded) return undefined;
        if (typeof context.loadFeatures === 'function') context.loadFeatures();
        const timer = setTimeout(() => setWaited(true), 3000);
        return () => clearTimeout(timer);
    }, [loaded]);

    const known = loaded || waited;
    // Chỉ coi là THIẾU khi máy chủ nói rõ là chưa cài (hasFeature trả false).
    const available = !hasFeature ? true : (known ? hasFeature(feature) : true);
    const missing = known && !!hasFeature && !hasFeature(feature);

    const packageName = typeof context.featurePackage === 'function'
        ? context.featurePackage(feature) : feature;

    return { available, missing, loading: !known, packageName, label: featureLabel(feature) };
}

/**
 * Tấm biển "cần cài gói mở rộng". Dùng riêng được, không nhất thiết qua FeatureGate.
 */
export function FeatureMissing({ feature, packageName, label, message, compact = false, onRetry }) {
    const name = label || featureLabel(feature);
    const pkg = packageName || feature;
    const [copied, setCopied] = useState(false);

    const copy = () => {
        const cmd = `yarn add ${pkg}`;
        try {
            if (navigator.clipboard) navigator.clipboard.writeText(cmd);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) { /* trình duyệt cũ không cho copy — bỏ qua, tên gói vẫn hiện trên màn hình */ }
    };

    if (compact) {
        return (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2 }}>
                <ExtensionOutlinedIcon sx={{ color: '#f57c00' }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                        Chưa cài gói mở rộng “{name}”
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {message || <>Máy chủ chưa cài gói <code>{pkg}</code> nên phần này chưa dùng được.</>}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Fade in timeout={200}>
            <Box sx={{ p: 3, height: '100%', minHeight: 260, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, textAlign: 'center', maxWidth: 460, bgcolor: 'white', boxShadow: '0 8px 32px rgba(0,0,0,0.05)' }}>
                    <Avatar sx={{ bgcolor: '#fff3e0', color: '#ef6c00', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <ExtensionOutlinedIcon fontSize="large" />
                    </Avatar>

                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#e65100' }}>
                        Cần cài gói mở rộng “{name}”
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
                        {message || <>Tính năng này nằm trong một gói mở rộng chưa được cài trên máy chủ.
                            Hãy đề nghị quản trị hệ thống cài gói dưới đây rồi khởi động lại máy chủ.</>}
                    </Typography>

                    <Chip
                        label={pkg}
                        onClick={copy}
                        onDelete={copy}
                        deleteIcon={<ContentCopyIcon fontSize="small" />}
                        sx={{ fontFamily: 'monospace', mb: 1 }}
                    />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                        {copied ? 'Đã chép lệnh cài đặt' : 'Bấm để chép lệnh cài đặt'}
                    </Typography>

                    {onRetry && (
                        <Button variant="outlined" size="small" onClick={onRetry}>
                            Kiểm tra lại
                        </Button>
                    )}
                </Paper>
            </Box>
        </Fade>
    );
}

/**
 * @param {string} feature   khoá tính năng, ví dụ 'usermodule' | 'marketing' | 'flow' | 'ai_agent'
 * @param {object} [context] context truyền qua prop (nếu màn hình không đọc AuthContext)
 * @param {node}   [fallback] nội dung thay thế tự chọn (bỏ trống -> dùng tấm biển mặc định)
 * @param {bool}   [compact]  hiện dạng dải nhỏ thay vì cả trang
 * @param {bool}   [silent]   thiếu gói thì ẩn hẳn, không báo gì (dùng cho nút/tab phụ)
 */
export default function FeatureGate({ feature, context, children, fallback, compact = false, silent = false, message }) {
    const { missing, loading, packageName, label } = useFeature(feature, context);

    if (loading) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: compact ? 60 : 200 }}>
                <CircularProgress size={compact ? 20 : 30} thickness={4} />
            </Box>
        );
    }

    if (missing) {
        if (silent) return null;
        if (fallback !== undefined) return fallback;
        return <FeatureMissing feature={feature} packageName={packageName} label={label} compact={compact} message={message} />;
    }

    return children || null;
}
